import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PDFDocument } from '../types';
import * as api from '../services/api';

const PublicPDFView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [doc, setDoc] = useState<PDFDocument | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDoc = async () => {
      if (!slug) return;

      try {
        const result = await api.getPublicDocument(slug);
        setDoc(result.document);
        setDownloadUrl(result.downloadUrl);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Document not found. The link might be broken or the document was removed.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium">Opening Document...</p>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full text-center bg-white p-10 rounded-3xl shadow-lg border border-slate-100">
          <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-exclamation-circle text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Oops!</h2>
          <p className="text-slate-500">{error}</p>
          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400">QR-PDF Linker - Document Persistence System</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-800">
      <header className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center flex-shrink-0">
            <i className="fas fa-file-pdf"></i>
          </div>
          <h1 className="text-sm font-semibold truncate">{doc.title}</h1>
        </div>
        <div className="flex gap-2">
          {downloadUrl && (
            <a
              href={downloadUrl}
              download={doc.fileName}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-xs font-bold rounded transition-colors flex items-center gap-2"
            >
              <i className="fas fa-download"></i> <span className="hidden sm:inline">Download</span>
            </a>
          )}
        </div>
      </header>

      <div className="flex-grow relative bg-slate-700 flex flex-col">
        {downloadUrl ? (
          <iframe
            src={`${downloadUrl}#toolbar=1`}
            className="w-full h-full border-none flex-grow"
            title={doc.title}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-white">
            PDF Viewer failed to load content.
          </div>
        )}
      </div>

      <div className="bg-white p-4 sm:p-6 border-t shadow-2xl">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Document Information</h2>
            <p className="text-sm text-slate-500">Updated: {new Date(doc.lastUpdated).toLocaleDateString()}</p>
          </div>
          {doc.summary && (
            <div className="flex-grow sm:mx-8">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">AI Summary</p>
              <p className="text-sm text-slate-700 leading-relaxed italic">{doc.summary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicPDFView;
